import {Injectable, Injector} from '@angular/core';
import {HosBaseService} from './hos-base.service';
import {AppConstants} from '../../app.constants';
import {Observable} from 'rxjs';
import {HttpParams, HttpResponse} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {RequestsUtils} from '../../shared/utils/requests-utils';

/**
 * HOS API
 *
 *
 * OpenAPI spec version: 1.0
 * Contact: info@bayinteractive.com
 *
 */

@Injectable()
export class UserDeviceService extends HosBaseService {

  constructor(injector: Injector) {
    super(injector);
  }

  /**
   * Register a user device (or update it if already exists and not deleted)
   *
   * @param hwid The hardware ID of the device
   * @param platform The platform of the device (e.g., "ios", "android")
   * @param pushToken (Optional) The push notification token
   * @param hosPlatform (Optional) A list of device types the user has authenticated on (desktop|iphone|ipad|android|sonos)
   * @param extraHttpRequestParams Optional HTTP request parameters
   */
  public registerDevice(hwid: string, platform: number, pushToken?: string, hosPlatform?: string, extraHttpRequestParams?: any): Observable<void> {
    return this.registerDeviceWithHttpInfo(hwid, platform, pushToken, hosPlatform, extraHttpRequestParams).pipe(
      map((response) => {
        if (response.status === 204) {
          return undefined;
        } else {
          return response.body;
        }
      })
    );
  }

  /**
   * Unregister a user device (set its status to Unregistered)
   *
   * @param hwid The hardware ID of the device
   * @param extraHttpRequestParams Optional HTTP request parameters
   */
  public unregisterDevice(hwid: string, extraHttpRequestParams?: any): Observable<void> {
    return this.unregisterDeviceWithHttpInfo(hwid, extraHttpRequestParams).pipe(
      map((response) => {
        if (response.status === 204) {
          return undefined;
        } else {
          return response.body;
        }
      })
    );
  }

  /**
   * Delete a user device (set its status to Deleted)
   *
   * @param hwid The hardware ID of the device
   * @param extraHttpRequestParams Optional HTTP request parameters
   */
  public deleteDevice(hwid: string, extraHttpRequestParams?: any): Observable<void> {
    return this.deleteDeviceWithHttpInfo(hwid, extraHttpRequestParams).pipe(
      map((response) => {
        if (response.status === 204) {
          return undefined;
        } else {
          return response.body;
        }
      })
    );
  }

  /**
   * Register a user device (or update it if already exists and not deleted)
   *
   * @param hwid The hardware ID of the device
   * @param platform The platform of the device (e.g., "ios", "android")
   * @param pushToken (Optional) The push notification token
   * @param extraHttpRequestParams Optional HTTP request parameters
   */
  public registerDeviceWithHttpInfo(hwid: string, platform: number, pushToken?: string, hosPlatform?: string, extraHttpRequestParams?: any): Observable<HttpResponse<void>> {
    const path = this.getBasePath() + `/user-devices/register`;

    let queryParameters = new HttpParams();
    let headers = this.getDefaultHeaders();

    if (hwid == null || platform == null) {
      throw new Error('Missing required parameters when calling registerDevice.');
    }

    let consumes: string[] = [];
    let canConsumeForm = this.canConsumeForm(consumes);
    let formParams = new HttpParams();

    formParams = formParams.set('hwid', hwid);
    formParams = formParams.set('platform', platform);
    if (pushToken !== undefined && pushToken !== null) {
      formParams = formParams.set('pushToken', pushToken);
    }

    if (hosPlatform !== undefined && hosPlatform !== null) {
      queryParameters = queryParameters.set('hosPlatform', hosPlatform);
    }

    let requestOptions = {
      headers: headers,
      body: RequestsUtils.getJsonObjectFromFormParams(formParams),
      params: queryParameters
    };

    if (extraHttpRequestParams) {
      requestOptions = this.extendObj(requestOptions, extraHttpRequestParams);
    }

    return this.executeHttpRequest(AppConstants.RequestMethods.POST, path, requestOptions);
  }

  /**
   * Unregister a user device (set its status to Unregistered)
   *
   * @param hwid The hardware ID of the device
   * @param extraHttpRequestParams Optional HTTP request parameters
   */
  public unregisterDeviceWithHttpInfo(hwid: string, extraHttpRequestParams?: any): Observable<HttpResponse<void>> {
    const path = this.getBasePath() + `/user-devices/unregister`;

    let queryParameters = new HttpParams();
    let headers = this.getDefaultHeaders();

    if (hwid == null) {
      throw new Error('Missing required parameter hwid when calling unregisterDevice.');
    }

    let consumes: string[] = [];
    let formParams = new HttpParams();
    formParams = formParams.set('hwid', hwid);

    let requestOptions = {
      headers: headers,
      body: RequestsUtils.getJsonObjectFromFormParams(formParams),
      params: queryParameters
    };

    if (extraHttpRequestParams) {
      requestOptions = this.extendObj(requestOptions, extraHttpRequestParams);
    }

    return this.executeHttpRequest(AppConstants.RequestMethods.POST, path, requestOptions);
  }

  /**
   * Delete a user device (set its status to Deleted)
   *
   * @param hwid The hardware ID of the device
   * @param extraHttpRequestParams Optional HTTP request parameters
   */
  public deleteDeviceWithHttpInfo(hwid: string, extraHttpRequestParams?: any): Observable<HttpResponse<void>> {
    const path = this.getBasePath() + `/user-devices/delete`;

    let queryParameters = new HttpParams();
    let headers = this.getDefaultHeaders();

    if (hwid == null) {
      throw new Error('Missing required parameter hwid when calling deleteDevice.');
    }

    let consumes: string[] = [];
    let formParams = new HttpParams();
    formParams = formParams.set('hwid', hwid);

    let requestOptions = {
      headers: headers,
      body: RequestsUtils.getJsonObjectFromFormParams(formParams),
      params: queryParameters
    };

    if (extraHttpRequestParams) {
      requestOptions = this.extendObj(requestOptions, extraHttpRequestParams);
    }

    return this.executeHttpRequest(AppConstants.RequestMethods.POST, path, requestOptions);
  }

}
